import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Loader2, RefreshCw, Rocket, AlertCircle, Calendar as CalendarIcon, X, Undo2,
  CheckCircle2, Info, Zap, Clock3, AlertTriangle, Landmark, Settings2, Building2, BarChart3,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  loadPaymentsWizardAus, syncPaymentsWizardAus, syncStatusPaymentsWizardAus, checkLoandiskAusPayments, loadAusDueReconciliation, loadAusPayouts, tickAusPayout,
  postAllAusPayments, getAusPostAllStatus, postAnywayAusPayment,
  previewAusRollback, startAusRollback, getAusRollbackStatus,
  unmatchAusPayment, dismissAusPayment, undismissAusPayment, matchAusPayment, updateAgeingP2P,
  loadAusLateFailures, reverseAusLateFailure, ackAusLateFailure,
} from "@/lib/api";
import { useRegion } from "@/contexts/RegionContext";
import { setCollectionsRegion } from "@/lib/regionFmt";
import { AusMatchDialog, AusPaymentRow, AusFailedRow, AusInFlightRow, DueReconRow, PayoutCard, LateFailureBanner, fmtAUD, fmtDate, sydneyDateOf, SOURCE_LABEL } from "./LoanRepaymentsAusParts";
import { AusRepaymentSettingsSheet } from "./LoanRepaymentsAusSettings";
import { AusRepaymentPerformanceSheet } from "./LoanRepaymentsAusPerformance";
import { AirtableFilter, FilterChips, applyAirtableFilters } from "./AirtableFilter";
import { errorDetailMessage } from "@/lib/errorDetailMessage";

// Airtable-style filter — every variable on a GoCardless payment, shared by all three tabs.
const FILTER_FIELDS = [
  { key: "_charge_date",  label: "Charge date",            type: "date" },
  { key: "_cleared",      label: "Cleared (confirmed) date", type: "date" },
  { key: "_paid_out",     label: "Paid out date (arrives in bank)", type: "date" },
  { key: "_failed",       label: "Failed date",            type: "date" },
  { key: "_submitted",    label: "Submitted to bank date", type: "date" },
  { key: "_result_due",   label: "Bank result due date",   type: "date" },
  { key: "amount",        label: "Amount $",               type: "number" },
  { key: "_days_since",   label: "Days since charge",      type: "number" },
  { key: "source",        label: "Source",                 type: "select", options: [{ value: "gocardless_au", label: "GoCardless AU" }, { value: "capay_bank", label: "Capay bank credit" }] },
  { key: "scheme_label",  label: "Method / scheme",        type: "select", options: [{ value: "PayTo", label: "PayTo" }, { value: "Direct Debit", label: "Direct Debit" }, { value: "Bank credit", label: "Bank credit (Capay)" }] },
  { key: "_p2p",          label: "Promise to pay",         type: "select", options: [{ value: "yes", label: "Has pending promise (mark kept)" }, { value: "no", label: "No promise" }] },
  { key: "_match_type",   label: "Matched by",             type: "select", options: [
      { value: "sfa_ref", label: "SFA reference" }, { value: "company_name", label: "Payer name (auto)" }, { value: "manual", label: "Manual" }, { value: "none", label: "Not matched" }] },
  { key: "_status",       label: "Status",                 type: "select", options: [
      { value: "cleared", label: "Cleared (money collected)" }, { value: "paid_out", label: "Paid out to our bank" },
      { value: "failed", label: "Failed" }, { value: "at_bank", label: "In flight — at the bank" }, { value: "scheduled", label: "Scheduled — not yet submitted" }] },
  { key: "_ld_status",    label: "LoanDisk posting",       type: "select", options: [
      { value: "ready", label: "Ready to post" }, { value: "posted", label: "Posted by portal" }, { value: "settling", label: "BECS settling (held)" },
      { value: "duplicate", label: "Already in LoanDisk" }, { value: "unmatched", label: "Needs match" }] },
  { key: "_paid_out_yn",  label: "Paid out?",              type: "select", options: [{ value: "yes", label: "Yes" }, { value: "no", label: "Not yet" }] },
  { key: "_retry",        label: "GC will retry?",         type: "select", options: [{ value: "yes", label: "Yes" }, { value: "no", label: "No" }] },
  { key: "sfa_number",    label: "SFA number",             type: "text" },
  { key: "payer_name",    label: "Payer name (bank)",      type: "text" },
  { key: "payment_reference", label: "Payer reference (bank)", type: "text" },
  { key: "_borrower",     label: "Customer / borrower",    type: "text" },
  { key: "description",   label: "GC description",         type: "text" },
  { key: "payment_id",    label: "GC payment id",          type: "text" },
  { key: "_loan_app",     label: "LoanDisk loan",          type: "text" },
  { key: "failure_cause", label: "Failure reason",         type: "text" },
];
const dayDiff = (iso) => (iso ? Math.round((Date.parse(sydneyDateOf(new Date().toISOString())) - Date.parse(iso)) / 86_400_000) : null);
const normalise = (x, kind) => ({
  ...x,
  _charge_date: x.date || x.charge_date || "",
  _cleared: sydneyDateOf(x.confirmed_at), _paid_out: x.payout_arrival_date || sydneyDateOf(x.paid_out_at),
  _failed: sydneyDateOf(x.failed_at || x.failure?.at), _submitted: sydneyDateOf(x.submitted_at),
  _result_due: x.expected_result_date || "",
  _days_since: x.days_since_charge ?? dayDiff(x.date || x.charge_date),
  _status: kind === "failed" ? "failed"
    : kind === "in_flight" ? (x.gc_status === "submitted" ? "at_bank" : "scheduled")
    : (x.gc_status === "paid_out" ? "paid_out" : "cleared"),
  _ld_status: kind !== "confirmed" ? "" : (["matched", "manual_matched"].includes(x.status) ? "ready" : x.status),
  _paid_out_yn: (x.payout_arrival_date || x.paid_out_at) ? "yes" : "no",
  _retry: (x.will_attempt_retry ?? x.failure?.will_attempt_retry) ? "yes" : "no",
  _borrower: x.matched_loan?.borrower_name || x.borrower_name || "",
  _match_type: x.match_type || "none",
  _p2p: x.p2p_prompt ? "yes" : "no",
  source: x.source || "gocardless_au",
  payer_name: x.payer_name || "", payment_reference: x.payment_reference || "",
  _loan_app: x.matched_loan?.loan_application_id || x.loan_application_id || "",
  failure_cause: x.failure_cause || x.failure?.cause || "",
  description: x.description || "",
});
const passes = (items, kind, conds) => (conds.length ? applyAirtableFilters(items.map((x) => normalise(x, kind)), conds, FILTER_FIELDS) : items);

const sydneyISO = (offsetDays = 0) => {
  const now = new Date(new Date().toLocaleString("en-US", { timeZone: "Australia/Sydney" }));
  now.setDate(now.getDate() + offsetDays);
  const p = (n) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
};

const fmtSyncAt = (iso) => {
  if (!iso) return "never";
  try {
    return new Date(iso).toLocaleString("en-AU", {
      timeZone: "Australia/Sydney", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    }) + " Sydney";
  } catch { return iso; }
};

function SyncStrip({ lastSync, lastSyncCapay, capayMode, settings, onSync, syncing, onOpenSettings, onOpenPerformance }) {
  const s = lastSync || {};
  const c = lastSyncCapay || {};
  const line = (run, okText) => (
    <span className="text-slate-600">
      Last sync {fmtSyncAt(run.finished_at || run.started_at)}
      {run.status === "running" && " · running…"}
      {run.status === "done" && okText}
      {run.status === "failed" && <span className="text-red-700"> · failed: {run.error}</span>}
    </span>
  );
  return (
    <Card className="border-emerald-200 bg-emerald-50/40" data-testid="lra-sync-strip">
      <CardContent className="py-2.5 px-4 flex flex-wrap items-center justify-between gap-3">
        <div className="space-y-1 text-xs">
          <div className="flex items-center gap-2 flex-wrap" data-testid="lra-sync-gc-line">
            <Landmark className="w-4 h-4 text-emerald-700" />
            <span className="font-semibold text-emerald-900">GoCardless AU · PayTo + BECS Direct Debit</span>
            {line(s, s.payments != null ? ` · ${s.payments} payments mirrored` : "")}
            <Badge variant="outline" className="border-emerald-300 text-emerald-700 text-[10px]">full sync {settings?.gc_sync_time || "06:00"} Sydney · re-sync every {settings?.gc_intraday_minutes || 30} min</Badge>
          </div>
          <div className="flex items-center gap-2 flex-wrap" data-testid="lra-sync-capay-line">
            <Building2 className="w-4 h-4 text-teal-700" />
            <span className="font-semibold text-teal-900">Capay bank feed · inbound credits</span>
            {c.status ? line(c, c.deposits != null ? ` · ${c.deposits} deposits · ${c.matched ?? 0} matched` : "") : <span className="text-slate-600">never synced</span>}
            <Badge variant="outline" className="border-teal-300 text-teal-700 text-[10px]">polls every {settings?.capay_poll_minutes || 30} min</Badge>
            <Badge variant="outline" className={`text-[10px] ${capayMode === "live" ? "border-red-300 text-red-700 bg-red-50" : "border-slate-300 text-slate-600"}`} data-testid="lra-capay-mode-badge">
              {capayMode === "live" ? "LIVE" : "sandbox"}
            </Badge>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onSync("gocardless_au")} disabled={!!syncing} data-testid="lra-sync-btn">
            {syncing === "gocardless_au" ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
            Refresh GoCardless
          </Button>
          <Button size="sm" variant="outline" className="h-7 text-xs border-teal-300 text-teal-800 hover:bg-teal-50" onClick={() => onSync("capay_bank")} disabled={!!syncing} data-testid="lra-sync-capay-btn">
            {syncing === "capay_bank" ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
            Refresh Capay
          </Button>
          <Button size="sm" className="h-7 text-xs bg-slate-800 hover:bg-slate-900 text-white" onClick={onOpenPerformance} data-testid="lra-performance-btn">
            <BarChart3 className="w-3.5 h-3.5 mr-1" />Performance
          </Button>
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={onOpenSettings} title="Loan Repayments settings" data-testid="lra-settings-btn">
            <Settings2 className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function TabTotals({ label, cells, tone = "emerald", testid }) {
  const toneCls = { emerald: "text-emerald-800", red: "text-red-800", amber: "text-amber-800" }[tone];
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-2.5 border-t bg-slate-50 text-xs" data-testid={testid}>
      <span className="font-semibold uppercase tracking-wide text-[10px] text-muted-foreground">Totals · {label}</span>
      <div className="flex flex-wrap gap-5">
        {cells.map(([k, v]) => (
          <span key={k}><span className="text-muted-foreground">{k}</span> <b className={`tabular-nums ${toneCls}`}>{fmtAUD(v)}</b></span>
        ))}
      </div>
    </div>
  );
}

function Totals({ loading, counts, totals, inFlightAll }) {
  const cell = (v) => (loading ? <Loader2 className="w-5 h-5 animate-spin inline" /> : v);
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <Card className="border-slate-200" data-testid="lra-totals-confirmed">
        <CardContent className="py-3 px-4">
          <p className="text-[11px] uppercase text-muted-foreground tracking-wide">Confirmed collections</p>
          <p className="text-2xl font-bold tabular-nums">{cell(fmtAUD(totals.value_confirmed))}</p>
          <p className="text-[11px] text-muted-foreground">
            {counts.total} payments · PayTo {fmtAUD(totals.by_scheme?.PayTo)} · DD {fmtAUD(totals.by_scheme?.["Direct Debit"])} · Bank {fmtAUD(totals.by_source?.capay_bank)}
          </p>
        </CardContent>
      </Card>
      <Card className="border-emerald-200 bg-emerald-50/40" data-testid="lra-totals-posted">
        <CardContent className="py-3 px-4">
          <p className="text-[11px] uppercase text-emerald-700 tracking-wide">Posted to LoanDisk</p>
          <p className="text-2xl font-bold tabular-nums text-emerald-800">{cell(fmtAUD(totals.value_posted))}</p>
          <p className="text-[11px] text-emerald-700/70">{counts.posted || 0} row{counts.posted === 1 ? "" : "s"} · {counts.ready} ready to post</p>
        </CardContent>
      </Card>
      <Card className="border-red-200 bg-red-50/40" data-testid="lra-totals-failed">
        <CardContent className="py-3 px-4">
          <p className="text-[11px] uppercase text-red-700 tracking-wide">Failed · upcoming arrears</p>
          <p className="text-2xl font-bold tabular-nums text-red-800">{cell(fmtAUD(totals.value_failed))}</p>
          <p className="text-[11px] text-red-700/70">{counts.failed || 0} collection{counts.failed === 1 ? "" : "s"} in the selected dates</p>
        </CardContent>
      </Card>
      <Card className="border-amber-200 bg-amber-50/40" data-testid="lra-totals-inflight">
        <CardContent className="py-3 px-4">
          <p className="text-[11px] uppercase text-amber-700 tracking-wide">In flight (DD lag)</p>
          <p className="text-2xl font-bold tabular-nums text-amber-800">{cell(fmtAUD(totals.value_in_flight))}</p>
          <p className="text-[11px] text-amber-700/70">{counts.in_flight || 0} awaiting bank result{inFlightAll ? " · all outstanding" : ""}</p>
        </CardContent>
      </Card>
    </div>
  );
}

export default function LoanRepaymentsAUS() {
  const { user } = useAuth();
  const [date, setDate] = useState(() => {
    const q = new URLSearchParams(window.location.search).get("date");
    return /^\d{4}-\d{2}-\d{2}$/.test(q || "") ? q : sydneyISO(-1);
  });
  const [rangeMode, setRangeMode] = useState(false);
  const [inFlightAll, setInFlightAll] = useState(true);
  const [basis, setBasis] = useState("charge");   // which date the date bar filters on
  const [due, setDue] = useState(null);
  const [dueLoading, setDueLoading] = useState(false);
  const [dueKind, setDueKind] = useState("all");   // all | missing | surplus | mismatch | matched
  const BASIS = [["charge", "Charge date"], ["cleared", "Cleared date"], ["paid_out", "Paid out (arrives in bank)"], ["failed", "Failed date"], ["submitted", "Submitted to bank"]];
  const basisLabel = BASIS.find(([k]) => k === basis)?.[1] || "Charge date";
  const [dateTo, setDateTo] = useState(date);
  const effTo = rangeMode ? (dateTo >= date ? dateTo : date) : null;
  const [tab, setTab] = useState("confirmed");
  const rangeLabel = effTo && effTo !== date ? `${fmtDate(date)} – ${fmtDate(effTo)}` : fmtDate(date);
  const [payouts, setPayouts] = useState(null);
  const [payoutsLoading, setPayoutsLoading] = useState(false);
  const [ticking, setTicking] = useState(null);
  const loadPayouts = useCallback(async () => {
    setPayoutsLoading(true);
    try { setPayouts(await loadAusPayouts(date, effTo)); }
    catch (e) { toast.error(errorDetailMessage(e, "Payouts failed to load")); }
    finally { setPayoutsLoading(false); }
  }, [date, effTo]);
  useEffect(() => { if (tab === "payouts") loadPayouts(); }, [tab, loadPayouts]);
  const tickPayout = async (po, ticked) => {
    setTicking(po.payout_id);
    try {
      await tickAusPayout(po.payout_id, { ticked });
      toast.success(ticked ? `${po.reference} reconciled` : `${po.reference} un-ticked`);
      await loadPayouts();
    } catch (e) { toast.error(errorDetailMessage(e, "Could not save")); }
    finally { setTicking(null); }
  };
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [checking, setChecking] = useState(false);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [schemeFilter, setSchemeFilter] = useState("all");   // all | PayTo | Direct Debit | Bank credit
  const [sourceFilter, setSourceFilter] = useState("all");   // all | gocardless_au | capay_bank
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [perfOpen, setPerfOpen] = useState(false);
  const [lateFailures, setLateFailures] = useState(null);
  const [lateBusy, setLateBusy] = useState(null);
  const loadLate = useCallback(() => loadAusLateFailures().then(setLateFailures).catch(() => {}), []);
  useEffect(() => { loadLate(); }, [loadLate]);
  const handleLate = async (r, fn, okMsg) => {
    setLateBusy(r.payment_id);
    try { await fn(r.payment_id, { actioning_user: user?.email || "" }); toast.success(okMsg); await loadLate(); await load(true); }
    catch (e) { toast.error(errorDetailMessage(e, "Failed")); }
    finally { setLateBusy(null); }
  };
  const [markingP2P, setMarkingP2P] = useState(null);
  const { currentRegion } = useRegion();
  setCollectionsRegion(currentRegion);   // ageing calls (P2P kept) tagged region=AUS
  const [conds, setConds] = useState([]);                   // Airtable-style conditions (AND)
  const bySch = (x) => (schemeFilter === "all" || x.scheme_label === schemeFilter) && (sourceFilter === "all" || (x.source || "gocardless_au") === sourceFilter);
  const sumAmt = (arr) => arr.reduce((t, x) => t + Number(x.amount || 0), 0);
  const [matchRow, setMatchRow] = useState(null);
  const [postingAnyway, setPostingAnyway] = useState(null);
  const [posting, setPosting] = useState(false);
  const [postProgress, setPostProgress] = useState(null);
  const [postResult, setPostResult] = useState(null);
  const [confirmPost, setConfirmPost] = useState(false);
  const [undoPreview, setUndoPreview] = useState(null);
  const [undoOpen, setUndoOpen] = useState(false);
  const [undoing, setUndoing] = useState(false);
  const [undoProgress, setUndoProgress] = useState(null);
  const loadSeq = useRef(0);          // newest load wins (see load())
  const loadRef = useRef(() => {});   // always the latest load() for timers / long polls

  const load = useCallback(async (silent = false) => {
    // Dean (12 Oct 2026): switching the date used to need several tries — slow responses for the OLD date (and the LoanDisk
    // check loop / 25 s GC re-sync reload, both closed over the old date) kept overwriting the new date's rows. Every load
    // now carries a sequence number and only the newest one may write to the page.
    const seq = ++loadSeq.current;
    if (!silent) setLoading(true); else setRefreshing(true);
    try {
      const d = await loadPaymentsWizardAus(date, effTo, inFlightAll, basis);
      if (seq !== loadSeq.current) return;   // a newer date / filter has been picked since — drop this response
      setData(d);
      if (d.auto_synced) {   // mirror was stale → backend kicked a GC re-sync; pull the fresh rows in shortly (for whatever date is showing THEN)
        toast.info("Pulling the latest from GoCardless…", { duration: 4000 });
        setTimeout(() => { if (document.visibilityState === "visible") loadRef.current(true); }, 25000);
      }
    }
    catch (e) { if (seq === loadSeq.current) toast.error(errorDetailMessage(e, "Failed to load GoCardless payments")); }
    finally { if (seq === loadSeq.current) { setLoading(false); setRefreshing(false); } }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, effTo, inFlightAll, basis]);
  loadRef.current = load;
  useEffect(() => { load(); }, [load]);

  // Page auto-refresh (admin setting) — silent reload while the tab is visible
  const refreshSecs = Number(data?.settings?.page_refresh_seconds ?? 0);
  useEffect(() => {
    if (!refreshSecs) return undefined;
    const id = setInterval(() => { if (document.visibilityState === "visible") load(true); }, refreshSecs * 1000);
    return () => clearInterval(id);
  }, [refreshSecs, load]);

  // LoanDisk due vs GoCardless reconciliation — lazy, only when the tab is open
  useEffect(() => {
    if (tab !== "due") return;
    let alive = true;
    setDueLoading(true); setDue(null);
    loadAusDueReconciliation(date, effTo)
      .then((r) => { if (alive) setDue(r); })
      .catch((e) => toast.error(errorDetailMessage(e, "LoanDisk due report failed")))
      .finally(() => { if (alive) setDueLoading(false); });
    return () => { alive = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, date, effTo]);

  // LoanDisk duplicate pre-check — auto-runs whenever ready rows haven't been
  // checked yet, then reloads so historic manual entries show as "Already in LoanDisk".
  const runCheck = useCallback(async (force = false) => {
    if (checking) return;
    setChecking(true);
    const mySeq = loadSeq.current;   // the date/filters this check was started for
    try {
      await checkLoandiskAusPayments({ date, date_to: effTo, force });
      const deadline = Date.now() + 3 * 60 * 1000;
      while (Date.now() < deadline) {
        await new Promise((res) => setTimeout(res, 2500));
        if (loadSeq.current !== mySeq) break;   // user moved to another date — stop writing the old date's rows
        const r = await loadPaymentsWizardAus(date, effTo, inFlightAll, basis);
        if (loadSeq.current !== mySeq) break;
        setData(r);
        if (!(r.counts?.unchecked > 0)) break;
      }
    } catch (e) { if (loadSeq.current === mySeq) toast.error(errorDetailMessage(e, "LoanDisk check failed")); }
    finally { setChecking(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, effTo, checking]);
  useEffect(() => {
    if (!loading && data?.counts?.unchecked > 0 && !checking) runCheck(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, data?.counts?.unchecked]);

  const refreshUndo = useCallback(() => { previewAusRollback(date, effTo).then(setUndoPreview).catch(() => {}); }, [date, effTo]);
  useEffect(() => { refreshUndo(); }, [refreshUndo]);

  const runSync = async (source = "gocardless_au") => {
    const capay = source === "capay_bank";
    setSyncing(source);
    try {
      const r = await syncPaymentsWizardAus(source);
      toast.info(r.already_running ? "A sync is already running" : capay ? "Pulling the last 10 days of bank credits from Capay…" : "Pulling the last 10 days from GoCardless AU…");
      const deadline = Date.now() + 4 * 60 * 1000;
      while (Date.now() < deadline) {
        await new Promise((res) => setTimeout(res, 2000));
        const st = await syncStatusPaymentsWizardAus();
        const s = capay ? st.last_sync_capay : st.last_sync;
        if (s && s.status !== "running") {
          if (s.status === "done") toast.success(capay ? `Synced ${s.deposits} bank credits from Capay · ${s.matched ?? 0} matched` : `Synced ${s.payments} payments from GoCardless`);
          else toast.error(`Sync failed: ${s.error}`);
          break;
        }
      }
      await loadRef.current(true);
    } catch (e) { toast.error(errorDetailMessage(e, "Sync failed")); }
    finally { setSyncing(false); }
  };
  const handleMarkP2PKept = async (row) => {
    const p = row.p2p_prompt;
    setMarkingP2P(row.payment_id);
    try {
      await updateAgeingP2P(p.case_id, p.p2p_id, { status: "kept", kept_amount: row.amount, kept_at: row.date,
        notes: `Kept via ${row.source === "capay_bank" ? "Capay bank credit" : `GoCardless ${row.scheme_label}`} ${fmtAUD(row.amount)} on ${fmtDate(row.date)} (AUS Loan Repayments)` });
      toast.success(`Promise marked kept — ${fmtAUD(p.promised_amount)} due ${fmtDate(p.promised_at)}`);
      await load(true);
    } catch (e) { toast.error(errorDetailMessage(e, "Could not mark the promise kept")); }
    finally { setMarkingP2P(null); }
  };
  const handlePickSuggestion = async (row, sg) => {
    try {
      await matchAusPayment(row.payment_id, { loan_id: sg.loan_id, loan_application_id: sg.loan_application_id, borrower_name: sg.borrower_name });
      toast.success(`Matched to ${sg.loan_application_id}`); await load(true);
    } catch (e) { toast.error(errorDetailMessage(e, "Match failed")); }
  };

  const counts = data?.counts || { total: 0, ready: 0, unmatched: 0, duplicate: 0, posted: 0, failed: 0, in_flight: 0 };
  const totals = data?.totals || {};
  const rows = data?.rows || [];
  const canPost = counts.ready > 0 && !posting && basis === "charge";

  const visibleRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return passes(rows, "confirmed", conds).filter((r) => {
      if (!bySch(r)) return false;
      if (statusFilter === "ready" && !["matched", "manual_matched"].includes(r.status)) return false;
      if (statusFilter !== "all" && statusFilter !== "ready" && r.status !== statusFilter) return false;
      if (!q) return true;
      return [r.description, r.sfa_number, r.payer_name, r.payment_reference, r.reference, r.matched_loan?.borrower_name, r.matched_loan?.loan_application_id, String(r.amount)]
        .filter(Boolean).join(" ").toLowerCase().includes(q);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, query, statusFilter, schemeFilter, sourceFilter, conds]);

  const filteredFailed = useMemo(() => {
    const q = query.trim().toLowerCase();
    return passes(data?.failed || [], "failed", conds).filter((f) => bySch(f) && (!q
      || [f.description, f.sfa_number, f.borrower_name, f.failure?.cause, String(f.amount)].filter(Boolean).join(" ").toLowerCase().includes(q)));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, query, schemeFilter, sourceFilter, conds]);
  const filteredInFlight = useMemo(() => passes(data?.in_flight || [], "in_flight", conds).filter(bySch),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [data, schemeFilter, sourceFilter, conds]);

  const handleUnmatch = async (row) => {
    try { await unmatchAusPayment(row.payment_id); toast.success("Match removed"); await load(true); }
    catch (e) { toast.error(errorDetailMessage(e, "Unmatch failed")); }
  };
  const handleDismiss = async (row) => {
    if (!window.confirm(`Hide ${fmtAUD(row.amount)} (${row.sfa_number || row.description}) from this list?`)) return;
    try { await dismissAusPayment(row.payment_id, {}); toast.success("Hidden"); await load(true); }
    catch (e) { toast.error(errorDetailMessage(e, "Dismiss failed")); }
  };
  const handleUndismiss = async (row) => {
    try { await undismissAusPayment(row.payment_id); await load(true); } catch { /* noop */ }
  };
  const handlePostAnyway = async (row) => {
    if (!window.confirm(`Post ${fmtAUD(row.amount)} to ${row.matched_loan?.loan_application_id} anyway?\n\nLoanDisk already has repayment #${row.duplicate_info?.existing_repayment_id} for the same loan/date/amount — this creates a SECOND entry.`)) return;
    setPostingAnyway(row.payment_id);
    try {
      const r = await postAnywayAusPayment(row.payment_id, { actioning_user: user?.email || "", date, date_to: effTo });
      toast.success(`Posted — LoanDisk repayment #${r.repayment_id || "created"}`);
      await load(true); refreshUndo();
    } catch (e) { toast.error(errorDetailMessage(e, "Post anyway failed")); }
    finally { setPostingAnyway(null); }
  };

  const pollRun = async (getStatus, runId, minutes) => {
    const deadline = Date.now() + minutes * 60 * 1000;
    while (Date.now() < deadline) {
      await new Promise((res) => setTimeout(res, 1500));
      let s; try { s = await getStatus(runId); } catch { continue; }
      if (s.status === "done" || s.status === "failed") return s;
      setPostProgress({ processed: s.processed, total: s.total, pct: s.progress_pct, preparing: s.status === "preparing" });
    }
    return null;
  };

  const runPostAll = async () => {
    setPosting(true); setPostResult(null); setConfirmPost(false);
    setPostProgress({ processed: 0, total: 0, pct: 0, preparing: true });
    try {
      const r = await postAllAusPayments({ actioning_user: user?.email || "", date, date_to: effTo });
      toast.info("Posting to LoanDisk AU — running in the background…");
      const final = await pollRun(getAusPostAllStatus, r.run_id, 15);
      if (final) {
        setPostResult(final);
        const skipped = final.skipped_duplicate_count || 0;
        if (final.status === "failed" && final.fatal_error) toast.error(final.fatal_error);
        else if (final.ok) toast.success(`Posted ${final.posted_count || 0} payment(s)` + (skipped ? ` · ${skipped} already in LoanDisk` : ""));
        else toast.warning(`${final.posted_count || 0} posted · ${final.failed_count || 0} failed` + (skipped ? ` · ${skipped} already in LoanDisk` : ""));
        await load(true); refreshUndo();
      } else toast.warning("Still running — it will finish in the background.");
    } catch (e) { toast.error(errorDetailMessage(e, "Post failed")); }
    finally { setPosting(false); setPostProgress(null); }
  };

  const runUndo = async () => {
    setUndoing(true); setUndoProgress(null);
    try {
      const r = await startAusRollback(date, user?.email || "", effTo);
      if (!r.run_id) { toast.info(r.message || "Nothing to undo"); return; }
      toast.info(`Deleting ${r.total} LoanDisk repayment(s)…`);
      const deadline = Date.now() + 10 * 60 * 1000;
      let final = null;
      while (Date.now() < deadline) {
        await new Promise((res) => setTimeout(res, 1500));
        let s; try { s = await getAusRollbackStatus(r.run_id); } catch { continue; }
        setUndoProgress({ processed: s.processed, total: s.total, pct: s.progress_pct });
        if (s.status === "done" || s.status === "failed") { final = s; break; }
      }
      if (final?.ok) toast.success(`Undone — ${final.deleted_count || 0} repayment(s) removed`);
      else if (final) toast.warning(`${final.deleted_count || 0} removed · ${final.failed_count || 0} failed`);
      setPostResult(null); await load(true); refreshUndo();
    } catch (e) { toast.error(errorDetailMessage(e, "Undo failed")); }
    finally { setUndoing(false); setUndoProgress(null); setUndoOpen(false); }
  };

  const setDay = (delta) => {
    const d = new Date(date + "T00:00:00"); d.setDate(d.getDate() + delta);
    const p = (n) => String(n).padStart(2, "0");
    setDate(`${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`);
  };

  const tabs = [
    { id: "confirmed", label: `Confirmed (${counts.total})`, icon: CheckCircle2 },
    { id: "failed", label: `Failed (${counts.failed})`, icon: AlertTriangle },
    { id: "in_flight", label: `In flight (${counts.in_flight})`, icon: Clock3 },
    { id: "due", label: due ? `LoanDisk due (${due.totals.due_count})` : "LoanDisk due", icon: Landmark },
    { id: "payouts", label: payouts ? `Payouts (${payouts.totals.ticked}/${payouts.totals.count})` : "Payouts", icon: CheckCircle2 },
  ];
  const dueRows = due ? [...(dueKind === "all" || dueKind === "missing" ? due.missing : []),
                          ...(dueKind === "all" || dueKind === "mismatch" ? due.mismatch : []),
                          ...(dueKind === "all" || dueKind === "surplus" ? due.surplus : []),
                          ...(dueKind === "all" || dueKind === "matched" ? due.matched : [])].filter(bySch2) : [];
  function bySch2(r) {
    if (schemeFilter === "all") return true;
    return (r.gc_payments || []).some((p) => p.scheme_label === schemeFilter);
  }

  return (
    <div className="space-y-4" data-testid="aus-loan-repayments-page">
      <div>
        <h1 className="text-xl font-bold text-foreground flex items-center gap-2">
          Loan Repayments <Badge className="bg-emerald-600 hover:bg-emerald-600 text-[10px]">AUS</Badge>
        </h1>
        <p className="text-sm text-muted-foreground">
          GoCardless AU (PayTo / Direct Debit) and the Capay bank feed (inbound credits) land here → review → post to LoanDisk AU in one click.
          Failed collections are the upcoming-arrears bucket.
        </p>
      </div>

      <SyncStrip lastSync={data?.last_sync} lastSyncCapay={data?.last_sync_capay} capayMode={data?.capay_mode} settings={data?.settings}
        onSync={runSync} syncing={syncing} onOpenSettings={() => setSettingsOpen(true)} onOpenPerformance={() => setPerfOpen(true)} />
      <LateFailureBanner data={lateFailures} busy={lateBusy}
        onReverse={(r) => window.confirm(`Delete LoanDisk repayment #${r.repayment_id} (${fmtAUD(r.amount)} on ${fmtDate(r.date)}) for ${r.sfa_number}?`) && handleLate(r, reverseAusLateFailure, "Reversed in LoanDisk")}
        onAck={(r) => handleLate(r, ackAusLateFailure, "Marked as already fixed")} />
      <Totals loading={loading} counts={counts} totals={totals} inFlightAll={inFlightAll} />

      <Card className="border-slate-200">
        <CardContent className="py-3 px-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            <CalendarIcon className="w-4 h-4 text-muted-foreground" />
            <select value={basis} onChange={(e) => setBasis(e.target.value)} className="h-8 text-xs border rounded px-1.5 bg-white font-medium" title="Which date the date bar filters on" data-testid="lra-basis-select">
              {BASIS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            {!rangeMode && <Button variant="outline" size="sm" className="h-8 px-2" onClick={() => setDay(-1)} data-testid="lra-day-prev-btn">‹</Button>}
            <Input type="date" value={date} onChange={(e) => { if (/^\d{4}-\d{2}-\d{2}$/.test(e.target.value)) setDate(e.target.value); }} className="w-40 h-8" data-testid="lra-date-input" />
            {rangeMode && (
              <>
                <span className="text-xs text-muted-foreground">to</span>
                <Input type="date" value={dateTo} min={date} onChange={(e) => { if (/^\d{4}-\d{2}-\d{2}$/.test(e.target.value)) setDateTo(e.target.value); }} className="w-40 h-8" data-testid="lra-date-to-input" />
              </>
            )}
            {!rangeMode && <Button variant="outline" size="sm" className="h-8 px-2" onClick={() => setDay(1)} disabled={date >= sydneyISO(0)} data-testid="lra-day-next-btn">›</Button>}
            <Button variant={rangeMode ? "default" : "outline"} size="sm" className={`h-8 text-xs ${rangeMode ? "bg-slate-800 hover:bg-slate-700" : ""}`}
              onClick={() => { if (!rangeMode) setDateTo(sydneyISO(-1) >= date ? sydneyISO(-1) : date); setRangeMode((m) => !m); }} data-testid="lra-range-toggle-btn">
              {rangeMode ? "Single day" : "Range"}
            </Button>
            <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => { setRangeMode(false); setDate(sydneyISO(-1)); }} data-testid="lra-yesterday-btn">Yesterday</Button>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => load(true)} disabled={refreshing || loading} title="Reload" data-testid="lra-refresh-btn">
              {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </Button>
            <span className="text-[11px] text-muted-foreground ml-1" data-testid="lra-range-label">{basisLabel.toLowerCase()}{rangeMode ? " range" : ""} · Sydney{data?.today_sydney ? ` (today ${fmtDate(data.today_sydney)})` : ""} · applies to Confirmed, Failed and In flight</span>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <Badge variant="outline" className="border-slate-300" data-testid="lra-count-total">{counts.total} confirmed</Badge>
            <Badge variant="outline" className="bg-emerald-50 border-emerald-300 text-emerald-700" data-testid="lra-count-ready">{counts.ready} ready</Badge>
            {counts.settling > 0 && <Badge variant="outline" className="bg-sky-50 border-sky-300 text-sky-700" title="Direct Debit confirmed by GoCardless but not yet paid out — held so a late failure can't be posted" data-testid="lra-count-settling">{counts.settling} BECS settling</Badge>}
            <Badge variant="outline" className="bg-amber-50 border-amber-300 text-amber-700" data-testid="lra-count-unmatched">{counts.unmatched} need match</Badge>
            {counts.duplicate > 0 && <Badge variant="outline" className="bg-blue-50 border-blue-300 text-blue-700" data-testid="lra-count-duplicate">{counts.duplicate} already in LoanDisk</Badge>}
            {counts.posted > 0 && <Badge variant="outline" className="bg-slate-50 border-slate-300" data-testid="lra-count-posted">{counts.posted} posted</Badge>}
            <Badge variant="outline" className={`${checking || counts.unchecked > 0 ? "bg-amber-50 border-amber-300 text-amber-700" : "bg-slate-50 border-slate-300 text-slate-600"} gap-1`}
              title="Every confirmed payment is checked against LoanDisk AU for an existing repayment on the same loan, charge date and amount before it can be posted" data-testid="lra-ld-check-badge">
              {checking || counts.unchecked > 0 ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3 text-emerald-600" />}
              {checking || counts.unchecked > 0 ? `checking LoanDisk (${counts.unchecked} left)` : "LoanDisk checked"}
            </Badge>
            <Button variant="ghost" size="sm" className="h-7 px-2 text-[11px]" onClick={() => runCheck(true)} disabled={checking || loading} title="Re-check every row against LoanDisk now" data-testid="lra-ld-recheck-btn">Re-check</Button>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" disabled={!canPost || checking || counts.unchecked > 0} onClick={() => setConfirmPost(true)} data-testid="lra-post-all-btn"
              title={basis !== "charge" ? "Switch the date bar back to Charge date to post" : counts.unchecked > 0 ? "Waiting for the LoanDisk duplicate check to finish" : ""}>
              <Rocket className="w-4 h-4 mr-1.5" />Post {counts.ready > 0 ? `${counts.ready} ready` : ""} to LoanDisk AU
            </Button>
            {undoPreview?.count > 0 && basis === "charge" && (
              <Button size="sm" variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" onClick={() => setUndoOpen(true)} disabled={undoing || posting} data-testid="lra-rollback-btn">
                <Undo2 className="w-4 h-4 mr-1.5" />Undo ({undoPreview.count})
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {postProgress && (
        <Alert className="border-blue-300 bg-blue-50" data-testid="lra-post-progress-banner">
          <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
          <AlertTitle className="text-blue-800">
            {postProgress.preparing || !postProgress.total ? "Preparing the list…" : `Posting to LoanDisk AU · ${postProgress.processed} / ${postProgress.total} (${postProgress.pct}%)`}
          </AlertTitle>
          <AlertDescription className="mt-2">
            <div className="w-full bg-blue-100 rounded-full h-2 overflow-hidden"><div className="h-full bg-blue-600 transition-all duration-500" style={{ width: `${postProgress.pct || 0}%` }} /></div>
          </AlertDescription>
        </Alert>
      )}
      {postResult?.failed?.length > 0 && (
        <Alert className="border-red-300 bg-red-50" data-testid="lra-post-fail-banner">
          <AlertCircle className="w-4 h-4 text-red-600" />
          <AlertTitle className="text-red-800 flex items-center justify-between">
            <span>Posting finished with {postResult.failed.length} failure(s)</span>
            <Button size="sm" variant="ghost" onClick={() => setPostResult(null)}><X className="w-3.5 h-3.5" /></Button>
          </AlertTitle>
          <AlertDescription className="mt-2 text-red-700 text-xs space-y-1 max-h-48 overflow-y-auto">
            {postResult.failed.map((f, i) => (
              <div key={i} className="flex justify-between gap-2 py-1 border-t border-red-200 first:border-t-0">
                <span>{f.loan_application_id || f.payment_id} · <b>{fmtAUD(f.amount)}</b></span>
                <span className="max-w-[60%] truncate" title={f.error}>{f.error}</span>
              </div>
            ))}
          </AlertDescription>
        </Alert>
      )}
      {postResult?.ok && postResult.posted_count > 0 && (
        <Alert className="border-emerald-300 bg-emerald-50" data-testid="lra-post-success-banner">
          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
          <AlertTitle className="text-emerald-800 flex items-center justify-between">
            <span>Posted {postResult.posted_count} payment(s) to LoanDisk AU</span>
            <Button size="sm" variant="ghost" onClick={() => setPostResult(null)}><X className="w-3.5 h-3.5" /></Button>
          </AlertTitle>
          <AlertDescription className="text-emerald-700 text-xs">Dated the charge / receipt date · GC method {postResult.method_id_used} / collector {postResult.collector_id_used}{postResult.capay_method_id_used ? ` · Capay method ${postResult.capay_method_id_used} / collector ${postResult.capay_collector_id_used}` : ""}</AlertDescription>
        </Alert>
      )}
      {postResult?.skipped_duplicate_count > 0 && (
        <Alert className="border-blue-300 bg-blue-50" data-testid="lra-post-skipped-banner">
          <Info className="w-4 h-4 text-blue-600" />
          <AlertTitle className="text-blue-800">{postResult.skipped_duplicate_count} payment(s) already in LoanDisk — flagged below, use Post anyway if it really is a separate payment</AlertTitle>
        </Alert>
      )}

      <Card className="border-slate-200">
        <div className="flex items-center justify-between border-b px-2">
          <div className="flex" data-testid="lra-tabs">
            {tabs.map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`lra-tab-${t.id}`}
                className={`flex items-center gap-1.5 px-4 py-2.5 text-xs border-b-2 -mb-px transition ${tab === t.id ? "border-emerald-600 font-semibold text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
                <t.icon className="w-3.5 h-3.5" />{t.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 py-1.5">
            <div className="flex rounded-md border overflow-hidden text-[11px]" data-testid="lra-source-filter">
              {[["all", "All sources"], ["gocardless_au", "GoCardless"], ["capay_bank", "Capay bank"]].map(([v, l]) => (
                <button key={v} onClick={() => setSourceFilter(v)} data-testid={`lra-source-${v === "all" ? "all" : v === "capay_bank" ? "capay" : "gc"}`}
                  className={`px-2.5 py-1 transition ${sourceFilter === v ? (v === "capay_bank" ? "bg-teal-600 text-white" : "bg-slate-800 text-white") : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                  {l}
                </button>
              ))}
            </div>
            <div className="flex rounded-md border overflow-hidden text-[11px]" data-testid="lra-scheme-filter">
              {[["all", "All"], ["PayTo", "PayTo"], ["Direct Debit", "Direct Debit"], ["Bank credit", "Bank credit"]].map(([v, l]) => (
                <button key={v} onClick={() => setSchemeFilter(v)} data-testid={`lra-scheme-${v === "all" ? "all" : v === "PayTo" ? "payto" : v === "Direct Debit" ? "dd" : "bank"}`}
                  className={`px-2.5 py-1 transition ${schemeFilter === v ? (v === "PayTo" ? "bg-violet-600 text-white" : v === "Direct Debit" ? "bg-sky-600 text-white" : v === "Bank credit" ? "bg-teal-600 text-white" : "bg-slate-800 text-white") : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                  {l}
                </button>
              ))}
            </div>
            {tab === "confirmed" && (
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-7 text-xs border rounded px-1.5 bg-white" data-testid="lra-status-filter">
                <option value="all">All</option><option value="ready">Ready</option><option value="unmatched">Need match</option>
                <option value="duplicate">Already in LoanDisk</option><option value="posted">Posted</option><option value="settling">BECS settling</option>
              </select>
            )}
            {tab === "failed" && <span className="text-[11px] text-muted-foreground" data-testid="lra-window-label">{rangeLabel}</span>}
            {tab === "due" && (
              <select value={dueKind} onChange={(e) => setDueKind(e.target.value)} className="h-7 text-xs border rounded px-1.5 bg-white" data-testid="lra-due-kind-select">
                <option value="all">All</option><option value="missing">Due · no GC collection</option><option value="surplus">GC only · not due</option>
                <option value="mismatch">Amount differs</option><option value="matched">Matched</option>
              </select>
            )}
            {tab === "in_flight" && (
              <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground cursor-pointer" data-testid="lra-inflight-all-toggle">
                <input type="checkbox" checked={inFlightAll} onChange={(e) => setInFlightAll(e.target.checked)} className="accent-amber-600" />
                {inFlightAll ? "Everything still at the bank (usually ~2 days of DDs)" : `Only charge dates ${rangeLabel}`}
              </label>
            )}
            <Input placeholder="Search SFA, name, amount…" value={query} onChange={(e) => setQuery(e.target.value)} className="h-7 w-52 text-xs" data-testid="lra-search-input" />
            <div data-testid="lra-airtable-filter"><AirtableFilter fields={FILTER_FIELDS} value={conds} onChange={setConds} /></div>
          </div>
        </div>
        {conds.length > 0 && (
          <div className="px-3 py-1.5 border-b bg-slate-50/60" data-testid="lra-filter-chips">
            <FilterChips conditions={conds} fields={FILTER_FIELDS} onRemove={(id) => setConds((c) => c.filter((x) => x.id !== id))} onClear={() => setConds([])} />
          </div>
        )}
        <CardContent className="p-0">
          {loading ? (
            <div className="p-10 text-center text-sm text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading GoCardless payments…</div>
          ) : tab === "confirmed" ? (
            <>
              {visibleRows.length === 0 && (
                <div className="p-8 text-center text-sm text-muted-foreground" data-testid="lra-empty">
                  No confirmed collections for {rangeLabel}{rows.length === 0 && " — weekend / no charge date, or hit Refresh from GoCardless"}.
                </div>
              )}
              {visibleRows.map((r) => (
                <AusPaymentRow key={r.payment_id} row={r} onMatch={() => setMatchRow(r)} onUnmatch={() => handleUnmatch(r)} onPickSuggestion={handlePickSuggestion} onMarkP2PKept={handleMarkP2PKept} markingP2P={markingP2P}
                  onDismiss={() => handleDismiss(r)} onPostAnyway={() => handlePostAnyway(r)} postingAnyway={postingAnyway === r.payment_id} />
              ))}
              {visibleRows.length > 0 && (
                <TabTotals testid="lra-totals-row-confirmed" label={`${visibleRows.length} confirmed${schemeFilter !== "all" ? ` · ${schemeFilter}` : ""}${sourceFilter !== "all" ? ` · ${SOURCE_LABEL[sourceFilter]}` : ""}`}
                  cells={[["Total", sumAmt(visibleRows)], ["GoCardless", sumAmt(visibleRows.filter((r) => r.source !== "capay_bank"))], ["Capay bank", sumAmt(visibleRows.filter((r) => r.source === "capay_bank"))], ["Ready", sumAmt(visibleRows.filter((r) => ["matched", "manual_matched"].includes(r.status)))],
                          ["Posted", sumAmt(visibleRows.filter((r) => r.status === "posted"))], ["Already in LoanDisk", sumAmt(visibleRows.filter((r) => r.status === "duplicate"))]]} />
              )}
              {(data?.dismissed_rows || []).length > 0 && (
                <div className="px-4 py-2 text-[11px] text-muted-foreground border-t bg-slate-50 flex flex-wrap gap-2 items-center" data-testid="lra-dismissed-strip">
                  {data.dismissed_rows.length} hidden:
                  {data.dismissed_rows.map((r) => (
                    <button key={r.payment_id} className="underline underline-offset-2 hover:text-slate-800" onClick={() => handleUndismiss(r)} title="Un-hide">
                      {r.sfa_number || r.payment_id} {fmtAUD(r.amount)}
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : tab === "payouts" ? (
            <>
              {payoutsLoading && !payouts && <div className="p-8 text-center text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading GoCardless payouts…</div>}
              {payouts && (
                <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-2.5 border-b bg-slate-50/70 text-xs" data-testid="lra-payouts-summary">
                  <span><b>{payouts.totals.count}</b> deposits arriving {rangeLabel} · <b className="tabular-nums">{fmtAUD(payouts.totals.amount, 2)}</b></span>
                  <span className="text-emerald-700"><b>{payouts.totals.ticked}</b> reconciled · {fmtAUD(payouts.totals.ticked_amount, 2)} · <span className="text-muted-foreground">{payouts.totals.count - payouts.totals.ticked} to tick</span></span>
                </div>
              )}
              {payouts && payouts.payouts.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground" data-testid="lra-payouts-empty">No GoCardless deposits arriving {rangeLabel}. Tip: switch to Range to see a week.</div>}
              {(payouts?.payouts || []).map((po) => <PayoutCard key={po.payout_id} po={po} onTick={tickPayout} ticking={ticking === po.payout_id} />)}
            </>
          ) : tab === "due" ? (
            <>
              {dueLoading && <div className="p-8 text-center text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Pulling LoanDisk AU due list for {rangeLabel}…</div>}
              {due && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 px-4 py-3 border-b bg-slate-50/70 text-xs" data-testid="lra-due-summary">
                  <div><p className="text-[10px] uppercase text-muted-foreground">Due in LoanDisk</p><p className="font-bold text-base tabular-nums">{fmtAUD(due.totals.due_total)}</p><p className="text-muted-foreground">{due.totals.due_count} instalments</p></div>
                  <div><p className="text-[10px] uppercase text-muted-foreground">In GoCardless</p><p className="font-bold text-base tabular-nums">{fmtAUD(due.totals.gc_total)}</p><p className="text-muted-foreground">{due.totals.gc_count} payments</p></div>
                  <div><p className="text-[10px] uppercase text-emerald-700">Matched</p><p className="font-bold text-base tabular-nums text-emerald-800">{fmtAUD(due.totals.matched_total)}</p><p className="text-muted-foreground">{due.totals.matched_count} · {due.totals.mismatch_count} amount differ</p></div>
                  <div><p className="text-[10px] uppercase text-red-700">Missing in GC</p><p className="font-bold text-base tabular-nums text-red-800" data-testid="lra-due-missing-total">{fmtAUD(due.totals.missing_total)}</p><p className="text-muted-foreground">{due.totals.missing_count} due, nothing scheduled</p></div>
                  <div><p className="text-[10px] uppercase text-sky-700">Surplus in GC</p><p className="font-bold text-base tabular-nums text-sky-800" data-testid="lra-due-surplus-total">{fmtAUD(due.totals.surplus_total)}</p><p className="text-muted-foreground">{due.totals.surplus_count} not due in LoanDisk</p></div>
                </div>
              )}
              {due && dueRows.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground" data-testid="lra-due-empty">Nothing in this bucket.</div>}
              {dueRows.map((r) => <DueReconRow key={`${r.kind}-${r.sfa_number}`} row={r} />)}
            </>
          ) : tab === "failed" ? (
            <>
              {filteredFailed.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground" data-testid="lra-failed-empty">No failed collections for {rangeLabel}.</div>}
              {filteredFailed.map((f) => <AusFailedRow key={f.payment_id} item={f} />)}
              {filteredFailed.length > 0 && (
                <TabTotals testid="lra-totals-row-failed" tone="red" label={`${filteredFailed.length} failed${schemeFilter !== "all" ? ` · ${schemeFilter}` : ""}`}
                  cells={[["Total failed", sumAmt(filteredFailed)], ["PayTo", sumAmt(filteredFailed.filter((f) => f.scheme_label === "PayTo"))],
                          ["Direct Debit", sumAmt(filteredFailed.filter((f) => f.scheme_label === "Direct Debit"))], ["GC will retry", sumAmt(filteredFailed.filter((f) => f.will_attempt_retry))]]} />
              )}
            </>
          ) : (
            <>
              {filteredInFlight.length > 0 && (
                <div className="grid grid-cols-12 gap-3 px-4 py-1.5 border-b bg-slate-50 text-[10px] uppercase tracking-wide text-muted-foreground">
                  <div className="col-span-4">Customer</div><div className="col-span-2 text-right">Amount</div><div className="col-span-2">Dates (Sydney)</div><div className="col-span-2">Bank result</div><div className="col-span-2 text-right">Status</div>
                </div>
              )}
              {filteredInFlight.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground" data-testid="lra-inflight-empty">Nothing awaiting bank confirmation{inFlightAll ? "" : ` for ${rangeLabel}`}.</div>}
              {filteredInFlight.map((f) => <AusInFlightRow key={f.payment_id} item={f} />)}
              {filteredInFlight.length > 0 && (
                <TabTotals testid="lra-totals-row-inflight" tone="amber" label={`${filteredInFlight.length} in flight${schemeFilter !== "all" ? ` · ${schemeFilter}` : ""}`}
                  cells={[["Total at the bank", sumAmt(filteredInFlight)], ["PayTo", sumAmt(filteredInFlight.filter((f) => f.scheme_label === "PayTo"))],
                          ["Direct Debit", sumAmt(filteredInFlight.filter((f) => f.scheme_label === "Direct Debit"))], ["Result overdue", sumAmt(filteredInFlight.filter((f) => f.overdue))]]} />
              )}
            </>
          )}
        </CardContent>
      </Card>

      <AusMatchDialog open={!!matchRow} row={matchRow} onClose={() => setMatchRow(null)} actioningUser={user?.email || ""}
        onMatched={async () => { setMatchRow(null); await load(true); }} />
      <AusRepaymentSettingsSheet open={settingsOpen} onOpenChange={setSettingsOpen} onSaved={() => load(true)} />
      <AusRepaymentPerformanceSheet open={perfOpen} onOpenChange={setPerfOpen} />

      <Dialog open={confirmPost} onOpenChange={setConfirmPost}>
        <DialogContent data-testid="lra-confirm-post-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Zap className="w-4 h-4 text-emerald-600" />Post {counts.ready} payment(s) to LoanDisk AU?</DialogTitle>
            <DialogDescription>
              {fmtAUD(totals.value_ready)} of confirmed GoCardless collections for {rangeLabel} will be posted as repayments, each dated its own GoCardless charge date.
              Payments already posted (by id) are skipped; same-day duplicates in LoanDisk are flagged, never double-posted. You can Undo afterwards.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmPost(false)} data-testid="lra-confirm-post-cancel">Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={runPostAll} data-testid="lra-confirm-post-btn"><Rocket className="w-4 h-4 mr-1.5" />Post now</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={undoOpen} onOpenChange={(v) => !undoing && setUndoOpen(v)}>
        <DialogContent data-testid="lra-undo-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700"><Undo2 className="w-4 h-4" />Undo {undoPreview?.count} repayment(s) for {rangeLabel}?</DialogTitle>
            <DialogDescription>
              Deletes exactly the {fmtAUD(undoPreview?.total_amount)} this page posted to LoanDisk AU for {rangeLabel}. Nothing keyed manually is touched.
            </DialogDescription>
          </DialogHeader>
          {undoProgress && <div className="w-full bg-red-100 rounded-full h-2 overflow-hidden"><div className="h-full bg-red-600 transition-all" style={{ width: `${undoProgress.pct || 0}%` }} /></div>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setUndoOpen(false)} disabled={undoing}>Cancel</Button>
            <Button variant="destructive" onClick={runUndo} disabled={undoing} data-testid="lra-undo-confirm-btn">
              {undoing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Undo2 className="w-4 h-4 mr-1.5" />}Undo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
